import { useReducer, useRef, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { MediaDropPill, createUploadSession, type Rejection, type UploadItem } from '@arthelper/ui';
import Suggestions from './Suggestions';
import { SUGGESTION_GROUPS } from './constants';
import CompactAudioRecorder from '~/pages/onboarding/components/CompactAudioRecorder';
import { useChatUI } from '~/context/chatUI';
import { useTranslation } from 'react-i18next';

function isInFlight(item: UploadItem) {
  return item.phase === 'queued' || item.phase === 'transferring' || item.phase === 'processing';
}

// Consolidated state management with useReducer
function inputReducer(state: any, action: any) {
  switch (action.type) {
    case 'SET_INPUT':
      return { ...state, localInput: action.value };
    case 'SET_FOCUSED':
      return { ...state, isFocused: action.focused };
    case 'SYNC_INPUT':
      return { ...state, localInput: action.value };
    default:
      return state;
  }
}

function ChatInput(props: any) {
  const {
    initialInput,
    onInputSubmit,
    onInputUpdate,
    status,
    selectedFiles,
    onFilesSelected,
    onRemoveFile,
    error,
    showSuggestions,
    conversationId,
    onSuggestionClick,
    disabled,
  } = props;

  const { showImageUpload, showAudioRecorder } = useChatUI();
  const { t } = useTranslation('chat');
  const rejectionText = (rejection: Rejection) => {
    const reason =
      rejection.reason === 'custom'
        ? rejection.message
        : t(`media-drop.reason-${rejection.reason}`, { max: rejection.limit });
    return t('media-drop.rejected', { name: rejection.file.name, reason });
  };

  const [state, dispatch] = useReducer(inputReducer, {
    localInput: initialInput,
    isFocused: false,
  });

  // Generate random starting group and suggestion
  const initialGroupIndex = Math.floor(Math.random() * SUGGESTION_GROUPS.length);
  const initialGroup = SUGGESTION_GROUPS[initialGroupIndex];
  const initialSuggestionIndex = Math.floor(Math.random() * initialGroup.options.length);

  const [currentGroupIndex, setCurrentGroupIndex] = useState(initialGroupIndex);
  const [currentSuggestionTitleKey, setCurrentSuggestionTitleKey] = useState(
    initialGroup.options[initialSuggestionIndex].titleKey
  );
  const [currentSuggestionFallback, setCurrentSuggestionFallback] = useState(
    initialGroup.options[initialSuggestionIndex].title
  );

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [uploadSession] = useState(() => createUploadSession());
  const disposalTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (disposalTimer.current) clearTimeout(disposalTimer.current);
    return () => {
      disposalTimer.current = setTimeout(() => uploadSession.dispose(), 0);
    };
  }, [uploadSession]);

  const sessionItems = useSyncExternalStore(
    uploadSession.subscribe,
    uploadSession.getSnapshot,
    uploadSession.getSnapshot
  );

  useEffect(() => {
    if (selectedFiles.length === 0) {
      // A TIFF converting has no selectedFiles entry yet; only a sent (settled) tray is cleared.
      if (!uploadSession.getSnapshot().some(isInFlight)) uploadSession.clear();
      return;
    }
    const current = uploadSession.getSnapshot();
    const restored = selectedFiles
      .filter((file: any) => file.status === 'completed' && file.publicUrl)
      .filter(
        (file: any) => !current.some((item) => item.id === file.id || item.file === (file.sourceFile ?? file.file))
      )
      .map((file: any) => ({
        id: file.id,
        url: file.publicUrl,
        kind: file.type === 'image' ? ('image' as const) : ('file' as const),
        name: file.file?.name,
      }));
    if (restored.length) uploadSession.addExisting(restored);
  }, [selectedFiles, uploadSession]);

  // Derived state
  const completedFiles = useMemo(() => selectedFiles.filter((f: any) => f.status === 'completed'), [selectedFiles]);
  const uploadingFiles = useMemo(() => selectedFiles.filter((f: any) => f.status === 'uploading'), [selectedFiles]);
  const isUploading = uploadingFiles.length > 0 || sessionItems.some(isInFlight);
  const canSubmit =
    !disabled &&
    (state.localInput.trim() || completedFiles.length > 0) &&
    (status === 'ready' || error) &&
    !isUploading;

  // Sync with parent
  useEffect(() => {
    dispatch({ type: 'SYNC_INPUT', value: initialInput });
  }, [initialInput]);

  // Auto-resize textarea
  useEffect(() => {
    if (!inputRef.current) return;
    inputRef.current.style.height = 'auto';
    inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 200)}px`;
  }, [state.localInput]);

  // Animated placeholder effect
  useEffect(() => {
    if (SUGGESTION_GROUPS.length === 0) return;

    const interval = setInterval(() => {
      setCurrentGroupIndex((prevGroupIndex) => {
        // Move to next group (cycle through all groups)
        const nextGroupIndex = (prevGroupIndex + 1) % SUGGESTION_GROUPS.length;
        const nextGroup = SUGGESTION_GROUPS[nextGroupIndex];

        // Select a random suggestion from the new group
        const randomSuggestionIndex = Math.floor(Math.random() * nextGroup.options.length);
        const randomSuggestion = nextGroup.options[randomSuggestionIndex];

        setCurrentSuggestionTitleKey(randomSuggestion.titleKey);
        setCurrentSuggestionFallback(randomSuggestion.title);

        return nextGroupIndex;
      });
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e: any) => {
    const newValue = e.target.value;
    dispatch({ type: 'SET_INPUT', value: newValue });
    onInputUpdate(newValue);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    onInputSubmit(state.localInput, e);
  };

  const handleKeyDown = (e: any) => {
    if (e.key !== 'Enter' || e.shiftKey) return;

    e.preventDefault();

    if (isUploading) {
      toast.info(t('please-wait-for-uploads'));
      return;
    }

    if (canSubmit) {
      onInputSubmit(state.localInput, e);
    }
  };

  const handleVoiceTranscription = (transcription: string) => {
    dispatch({ type: 'SET_INPUT', value: transcription });
    onInputUpdate(transcription);
  };

  return (
    <div className={`lg:px-4 pb-4 relative ${conversationId ? 'px-4' : ''}`}>
      <form ref={formRef} onSubmit={handleSubmit} className="max-w-3xl mx-auto">
        <div className="rounded-2xl border border-gray-200 shadow-md bg-white focus-within:border-gray-300 transition-colors">
          {showSuggestions && onSuggestionClick && (
            <Suggestions
              onSuggestionClick={onSuggestionClick}
              disabled={disabled || isUploading || status !== 'ready'}
            />
          )}

          <div className="relative">
            <textarea
              ref={inputRef}
              className="w-full px-4 py-4 focus:outline-hidden resize-none max-h-[200px] min-h-[60px] text-gray-900 border-none rounded-t-2xl text-base"
              value={state.localInput}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onFocus={() => dispatch({ type: 'SET_FOCUSED', focused: true })}
              onBlur={() => dispatch({ type: 'SET_FOCUSED', focused: false })}
              rows={1}
            />

            {/* Typewriter placeholder - only show when empty and not focused */}
            {!state.localInput.trim() && !state.isFocused && (
              <div className="absolute top-4 left-4 pointer-events-none">
                <div
                  key={selectedFiles.length > 0 ? 'files' : currentGroupIndex}
                  className="relative w-[max-content] before:absolute before:inset-0 before:bg-white before:animate-typewriter"
                >
                  <span className="text-gray-500">{t(currentSuggestionTitleKey, currentSuggestionFallback)}</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center px-4 pb-4 pt-2">
            {showImageUpload && (
              <MediaDropPill
                session={uploadSession}
                dropScope={formRef}
                pasteScope={formRef}
                label={t('input.upload')}
                overlayPrompt={t('input.add-to-message')}
                accept={['image/*', '.tif', '.tiff', '.pdf']}
                maxFiles={20}
                maxSizeMB={100}
                disabled={disabled}
                uploader={(file, ctx) => onFilesSelected([file], ctx)}
                onRemove={(item: UploadItem) => {
                  const selected = selectedFiles.find(
                    (entry: any) => entry.id === item.id || (entry.sourceFile ?? entry.file) === item.file
                  );
                  if (selected) onRemoveFile(selected.id);
                }}
                onRejected={(rejections) => {
                  for (const rejection of rejections) {
                    toast.error(rejectionText(rejection));
                  }
                }}
                labels={{
                  release: (count) => t('media-drop.release', { count }),
                  uploading: (done, total) => t('media-drop.uploading', { done, total }),
                  added: (count) => t('media-drop.added', { count }),
                  failed: t('media-drop.failed'),
                  dropMore: t('media-drop.drop-more'),
                  retry: t('media-drop.retry'),
                  remove: (name) => t('media-drop.remove', { name }),
                  chooseFiles: () => t('input.add-photos-and-files'),
                  status: {
                    queued: t('media-drop.queued'),
                    transferring: t('media-drop.transferring'),
                    processing: t('media-drop.processing'),
                    transcoding: t('media-drop.processing'),
                    done: t('media-drop.done'),
                    error: t('media-drop.failed'),
                  },
                  rejected: rejectionText,
                }}
              />
            )}

            <div className="flex items-center gap-2 ml-auto">
              {showAudioRecorder && (
                <CompactAudioRecorder setValue={handleVoiceTranscription} className="!bg-transparent" size="lg" />
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                className={`p-4 rounded-full transition-all ${
                  canSubmit
                    ? 'bg-gray-900 text-white hover:bg-gray-800'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="text-xs text-center bg-gray-100 rounded-bl-xl rounded-br-xl p-1 text-gray-500 hidden">
            ArtHelper Assistant is a beta feature. It may make mistakes. Please help us improve it by providing&nbsp;
            <a
              href="https://arthelper.canny.io/feature-requests"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              feedback
            </a>
            .
          </div>
        </div>
      </form>
    </div>
  );
}

export default ChatInput;
